'use client';

import { Alert, Box, Button, Stack, Typography } from '@mui/material';
import { useEffect, useState } from 'react';

import { Loader } from '@/components/layout/loading';
import { ContactSupportLink } from '@/components/support/support';
import { StepContainer } from '@/components/wizard/StepContainer';
import type { StepProps } from '@/components/wizard/StepProps';
import { useDAL } from '@/contexts/useDAL';
import { useUser } from '@/contexts/useUser';
import { PreferencesKey } from '@/data/dal/preferencesKey';

import { CheckResultRow } from '../CheckResultRow';
import type { RdsCdcWizardState } from '../rdsCdcWizardState';

export const FinalChecksStep = (props: StepProps<RdsCdcWizardState>) => {
  const { wizardState: state, setWizardState } = props;
  const dal = useDAL();
  const { isEmployee } = useUser();
  const { data: prefResponse } = dal.preferences.useUserPref(
    PreferencesKey.Preferences
  );
  const isDevMode = isEmployee && (prefResponse?.value?.isDevMode ?? false);
  const [completionError, setCompletionError] = useState<string | null>(null);

  const {
    data: checks,
    isValidating: loading,
    error: swrError,
    mutate: runChecks,
  } = dal.inventory.privateLink.useCheckCdcPrerequisites(state.resourceId);

  const error = swrError
    ? swrError instanceof Error
      ? swrError.message
      : 'Failed to run prerequisite checks'
    : null;

  const ramOk = checks?.ramShareCheck?.found && !checks?.ramShareCheck?.error;
  const ramArn = checks?.ramShareCheck?.ramResourceShareArn ?? '';
  const paramsOk = checks?.parameterChecks?.every((p) => p.ok) ?? false;
  const allPassed = ramOk && paramsOk;

  // Update wizard state when RAM share is found
  useEffect(() => {
    if (ramOk && ramArn) {
      setWizardState((prev) => ({
        ...prev,
        ramResourceShareArn: ramArn,
      }));
    }
  }, [ramOk, ramArn, setWizardState]);

  return (
    <StepContainer
      stepperIndex={2}
      canGoNext={() => !loading && !!allPassed && !!state.ramResourceShareArn}
      nextButtonText='Complete Setup'
      onNextClick={async () => {
        setCompletionError(null);
        try {
          await dal.inventory.privateLink.setupAwsPrivateLink(
            state.resourceId,
            { ramResourceShareArn: state.ramResourceShareArn }
          );
          await dal.inventory.configuration.update(state.resourceId, {
            setDbBackupModeCdc: true,
          });
          await props.finish();
        } catch (err) {
          setCompletionError(
            err instanceof Error ? err.message : 'Failed to complete CDC setup'
          );
        }
      }}
      onBackClick={props.back}
      sx={{ overflow: 'auto', paddingX: '0px', paddingY: 'min(60px, 4vw)' }}
    >
      <Box paddingX='min(100px, 6vw)'>
        <Stack gap='24px' maxWidth='720px'>
          <Stack gap='8px'>
            <Typography variant='h6'>Final Checks</Typography>
            <Typography variant='body2' color='text.secondary'>
              Verifying that all prerequisites are met before completing setup.
            </Typography>
          </Stack>

          {loading && (
            <Stack direction='row' alignItems='center' gap='12px'>
              <Loader variant='inline' />
              <Typography variant='body2' color='text.secondary'>
                Running final checks...
              </Typography>
            </Stack>
          )}

          {error && <Alert severity='error'>{error}</Alert>}

          {completionError && (
            <Alert severity='error'>
              {completionError}. Please try again or <ContactSupportLink /> if
              the issue persists.
            </Alert>
          )}

          {checks && !loading && (
            <Stack gap='16px'>
              <CheckResultRow
                status={ramOk ? 'ok' : 'error'}
                label='RAM Resource Share'
              >
                {ramOk && (
                  <Typography variant='body2' color='text.secondary'>
                    Found: {checks.ramShareCheck?.ramResourceShareArn}
                  </Typography>
                )}
                {!ramOk && checks.ramShareCheck?.error && (
                  <Alert severity='error' sx={{ mt: '4px' }}>
                    {checks.ramShareCheck?.error}
                  </Alert>
                )}
                {!ramOk && !checks.ramShareCheck?.error && (
                  <Alert severity='error' sx={{ mt: '4px' }}>
                    RAM Resource Share not found. Please verify that you&apos;ve
                    completed the instructions in the PrivateLink step.
                  </Alert>
                )}
              </CheckResultRow>

              {checks.parameterChecks?.map((param) => (
                <CheckResultRow
                  key={param.parameterName}
                  status={param.ok ? 'ok' : 'error'}
                  label={param.parameterName ?? ''}
                >
                  {param.ok && (
                    <Typography variant='body2' color='text.secondary'>
                      Value: <code>{param.currentValue}</code> on parameter
                      group <code>{param.parameterGroupName}</code>
                    </Typography>
                  )}
                  {!param.ok && param.error && (
                    <Alert severity='error' sx={{ mt: '4px' }}>
                      {param.error}
                    </Alert>
                  )}
                  {!param.ok && !param.error && (
                    <Alert severity='warning' sx={{ mt: '4px' }}>
                      <Typography variant='body2'>
                        Set{' '}
                        <code>
                          {param.parameterName} = {param.requiredValue}
                        </code>{' '}
                        in the database&apos;s parameter group
                      </Typography>
                      <Typography variant='body2' sx={{ mt: '8px' }}>
                        This might require RDS instance reboot to take effect.
                      </Typography>
                    </Alert>
                  )}
                </CheckResultRow>
              ))}

              {(!allPassed || isDevMode) && !loading && (
                <Button
                  variant='outlined'
                  onClick={() => void runChecks()}
                  sx={{ alignSelf: 'flex-start' }}
                >
                  Re-check
                </Button>
              )}
            </Stack>
          )}
        </Stack>
      </Box>
    </StepContainer>
  );
};
